import { supabase } from '@/lib/supabase';

export interface AffiliateLimitsOverview {
  totalLimits: { daily: number; monthly: number };
  allocatedLimits: { daily: number; monthly: number };
  availableToAllocate: { daily: number; monthly: number };
  affiliateDefaults?: { daily: number; monthly: number };
  userAllocations: Array<{
    userId: string;
    userName: string;
    userEmail: string;
    daily: number;
    monthly: number;
    isCustom: boolean;
    lastUsed?: string;
  }>;
}

export interface UserLimitAllocation {
  userId: string;
  userName: string;
  userEmail: string;
  daily: number;
  monthly: number;
  dailyUsage: number;
  monthlyUsage: number;
  lastUsed?: string;
}

export interface AffiliateDefaultLimits {
  daily: number;
  monthly: number;
}

export class AffiliateUsageLimitsService {
  /**
   * Admin sets limits for affiliate
   */
  static async setAffiliateLimits(
    affiliateId: string, 
    daily: number, 
    monthly: number,
    adminId: string
  ): Promise<boolean> {
    try {
      
      
      const { error } = await supabase
        .from('affiliate_usage_limits')
        .upsert({
          affiliate_id: affiliateId,
          feature_type: 'market_analysis',
          daily_limit: daily,
          monthly_limit: monthly,
          set_by_admin: adminId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'affiliate_id,feature_type'
        });

      if (error) {
        console.error('❌ Error setting affiliate limits:', error);
        return false;
      }

      console.log('✅ Successfully set affiliate limits');
      return true;
    } catch (error) {
      console.error('❌ Exception setting affiliate limits:', error);
      return false;
    }
  }

  /**
   * Set affiliate's default limits for new users
   */
  static async setAffiliateDefaultLimits(
    affiliateId: string,
    daily: number,
    monthly: number
  ): Promise<boolean> {
    try {
      console.log('🎯 Setting affiliate default limits:', { affiliateId, daily, monthly });
      
      const { error } = await supabase
        .from('affiliates')
        .update({
          default_daily_limit: daily,
          default_monthly_limit: monthly,
          default_limits_updated_at: new Date().toISOString()
        })
        .eq('id', affiliateId);

      if (error) {
        console.error('❌ Error setting affiliate default limits:', error);
        return false;
      }

      console.log('✅ Successfully set affiliate default limits');
      return true;
    } catch (error) {
      console.error('❌ Exception setting affiliate default limits:', error);
      return false;
    }
  }

  /**
   * Get affiliate's default limits
   */
  static async getAffiliateDefaultLimits(affiliateId: string): Promise<AffiliateDefaultLimits | null> {
    try {
      const { data, error } = await supabase
        .from('affiliates')
        .select('default_daily_limit, default_monthly_limit')
        .eq('id', affiliateId)
        .single();

      if (error) {
        console.error('❌ Error fetching affiliate default limits:', error);
        return null;
      }

      if (!data.default_daily_limit || !data.default_monthly_limit) {
        return null; // No custom defaults set
      }

      return {
        daily: data.default_daily_limit,
        monthly: data.default_monthly_limit
      };
    } catch (error) {
      console.error('❌ Exception fetching affiliate default limits:', error);
      return null;
    }
  }

  /**
   * Apply affiliate's default limits to all existing users
   */
  static async applyDefaultsToAllUsers(
    affiliateId: string,
    daily: number,
    monthly: number
  ): Promise<boolean> {
    try {
      
      
      // First, set the affiliate's default limits
      const defaultsSet = await this.setAffiliateDefaultLimits(affiliateId, daily, monthly);
      if (!defaultsSet) return false;

      // Get all referred users for this affiliate (using proper referrals table)
      const { data: referrals, error: referralsError } = await supabase
        .from('referrals')
        .select('referred_user_id')
        .eq('referrer_id', affiliateId);

      if (referralsError) {
        console.error('❌ Error fetching referred users:', referralsError);
        return false;
      }

      if (!referrals || referrals.length === 0) {
        console.log('✅ No referred users to update');
        return true;
      }

      // Update/insert default limits for all users using UPSERT
      for (const referral of referrals) {
        const { error: upsertError } = await supabase
          .from('affiliate_user_limits')
          .upsert({
            affiliate_id: affiliateId,
            user_id: referral.referred_user_id,
            feature_type: 'market_analysis',
            daily_limit: daily,
            monthly_limit: monthly,
            is_default: true,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'affiliate_id,user_id,feature_type'
          });

        if (upsertError) {
          console.error(`❌ Error upserting limits for user ${referral.referred_user_id}:`, upsertError);
          // Continue with other users instead of failing completely
        }
      }

      console.log('✅ Successfully applied defaults to all users using upsert');
      return true;
    } catch (error) {
      console.error('❌ Exception applying defaults to all users:', error);
      return false;
    }
  }

  /**
   * Affiliate sets limits for their users (converts from default to custom)
   */
  static async setUserLimits(
    affiliateId: string,
    userId: string,
    daily: number,
    monthly: number
  ): Promise<boolean> {
    try {
      console.log('👥 Affiliate setting user limits:', { affiliateId, userId, daily, monthly });
      
      // First verify the user belongs to this affiliate
      const { data: referral, error: referralError } = await supabase
        .from('referrals')
        .select('referred_user_id')
        .eq('referred_user_id', userId)
        .eq('referrer_id', affiliateId)
        .single();

      if (referralError || !referral) {
        console.error('❌ User does not belong to this affiliate:', { userId, affiliateId });
        return false;
      }

      // Check if affiliate has enough remaining allocation
      const overview = await this.getAffiliateLimitsOverview(affiliateId);
      const currentUserAllocation = overview.userAllocations.find(u => u.userId === userId);
      
      // Calculate the difference (current allocation vs new allocation)
      const currentDaily = currentUserAllocation?.daily || 0;
      const currentMonthly = currentUserAllocation?.monthly || 0;
      const dailyDifference = daily - currentDaily;
      const monthlyDifference = monthly - currentMonthly;
      
      // Check if new allocation would exceed available limits
      if (dailyDifference > overview.availableToAllocate.daily || 
          monthlyDifference > overview.availableToAllocate.monthly) {
        console.error('❌ Not enough allocation remaining');
        return false;
      }

      const { error } = await supabase
        .from('affiliate_user_limits')
        .upsert({
          affiliate_id: affiliateId,
          user_id: userId,
          feature_type: 'market_analysis',
          daily_limit: daily,
          monthly_limit: monthly,
          is_default: false, // This is now a custom limit
          set_by_affiliate: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'affiliate_id,user_id,feature_type'
        });

      if (error) {
        console.error('❌ Error setting user limits:', error);
        return false;
      }

      console.log('✅ Successfully set user limits');
      return true;
    } catch (error) {
      console.error('❌ Exception setting user limits:', error);
      return false;
    }
  }

  /**
   * OPTIMIZED: Get affiliate's current limits and allocation overview using database function
   */
  static async getAffiliateLimitsOverview(affiliateId: string): Promise<AffiliateLimitsOverview> {
    try {
      
      
      // Use the existing database function - SINGLE QUERY instead of 30+ queries
      const { data, error } = await supabase
        .rpc('get_affiliate_usage_overview', {
          affiliate_uuid: affiliateId
        });

      if (error) {
        console.error('❌ Error calling get_affiliate_usage_overview:', error);
        // Fallback to default values
        return {
          totalLimits: { daily: 10, monthly: 100 },
          allocatedLimits: { daily: 0, monthly: 0 },
          availableToAllocate: { daily: 10, monthly: 100 },
          userAllocations: []
        };
      }

      console.log('✅ [OPTIMIZED] Database function returned:', data);

      // Parse the response from the database function
      const result = data || {};
      
      // Get affiliate's default limits separately (quick query)
      const affiliateDefaults = await this.getAffiliateDefaultLimits(affiliateId);

      const overview: AffiliateLimitsOverview = {
        totalLimits: result.totalLimits || { daily: 10, monthly: 100 },
        allocatedLimits: result.allocatedLimits || { daily: 0, monthly: 0 },
        availableToAllocate: result.availableToAllocate || { daily: 10, monthly: 100 },
        affiliateDefaults,
        userAllocations: result.userAllocations || []
      };

      console.log('✅ [OPTIMIZED] Single query completed successfully - Performance improved!');
      return overview;
    } catch (error) {
      console.error('❌ Exception getting affiliate overview:', error);
      // Return sensible defaults to avoid breaking the UI
      return {
        totalLimits: { daily: 10, monthly: 100 },
        allocatedLimits: { daily: 0, monthly: 0 },
        availableToAllocate: { daily: 10, monthly: 100 },
        userAllocations: []
      };
    }
  }

  /**
   * OPTIMIZED: Get all affiliates with their limits (for admin) using batch approach
   */
  static async getAllAffiliateLimits(): Promise<Array<{
    affiliateId: string;
    affiliateName: string;
    affiliateEmail: string;
    dailyLimit: number;
    monthlyLimit: number;
    allocatedDaily: number;
    allocatedMonthly: number;
    userCount: number;
  }>> {
    try {
      
      
      // Get all affiliates in one query
      const { data: affiliates, error: affiliatesError } = await supabase
        .from('affiliates')
        .select('id, name, email, status');

      if (affiliatesError) {
        console.error('❌ Error fetching affiliates:', affiliatesError);
        return [];
      }

      console.log(`🔍 Found ${affiliates?.length || 0} total affiliates`);

      // Get all affiliate usage limits in one query
      const { data: allLimits, error: limitsError } = await supabase
        .from('affiliate_usage_limits')
        .select('affiliate_id, daily_limit, monthly_limit')
        .eq('feature_type', 'market_analysis');

      if (limitsError) {
        console.error('❌ Error fetching affiliate limits:', limitsError);
      }

      // Get all user allocations in one query
      const { data: allAllocations, error: allocationsError } = await supabase
        .from('affiliate_user_limits')
        .select('affiliate_id, daily_limit, monthly_limit')
        .eq('feature_type', 'market_analysis');

      if (allocationsError) {
        console.error('❌ Error fetching user allocations:', allocationsError);
      }

      // Get user counts per affiliate in one query
      const { data: userCounts, error: countsError } = await supabase
        .from('referrals')
        .select('referrer_id')
        .then(({ data, error }) => {
          if (error) return { data: null, error };
          const counts = data?.reduce((acc, ref) => {
            acc[ref.referrer_id] = (acc[ref.referrer_id] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          return { data: counts, error: null };
        });

      if (countsError) {
        console.error('❌ Error fetching user counts:', countsError);
      }

      // Process results efficiently
      const results = (affiliates || []).map(affiliate => {
        const limits = allLimits?.find(l => l.affiliate_id === affiliate.id);
        const allocations = allAllocations?.filter(a => a.affiliate_id === affiliate.id) || [];
        const userCount = userCounts?.[affiliate.id] || 0;

        const allocatedDaily = allocations.reduce((sum, a) => sum + (a.daily_limit || 0), 0);
        const allocatedMonthly = allocations.reduce((sum, a) => sum + (a.monthly_limit || 0), 0);

        return {
          affiliateId: affiliate.id,
          affiliateName: affiliate.name,
          affiliateEmail: affiliate.email,
          dailyLimit: limits?.daily_limit || 10,
          monthlyLimit: limits?.monthly_limit || 100,
          allocatedDaily,
          allocatedMonthly,
          userCount
        };
      });

      console.log('✅ [OPTIMIZED] Batch processing completed - Much faster!');
      return results;
    } catch (error) {
      console.error('❌ Exception getting all affiliate limits:', error);
      return [];
    }
  }

  /**
   * Remove user limits (reset to affiliate defaults)
   */
  static async removeUserLimits(affiliateId: string, userId: string): Promise<boolean> {
    try {
      

      // Get affiliate's default limits
      const { data: effectiveDefaults, error: defaultsError } = await supabase.rpc(
        'get_affiliate_effective_defaults',
        { affiliate_uuid: affiliateId }
      );

      if (defaultsError) {
        console.error('❌ Error getting effective defaults:', defaultsError);
        return false;
      }

      const defaults = effectiveDefaults?.[0] || { daily_limit: 3, monthly_limit: 20 };

      // Update the record to use default limits using UPSERT
      const { error } = await supabase
        .from('affiliate_user_limits')
        .upsert({
          affiliate_id: affiliateId,
          user_id: userId,
          feature_type: 'market_analysis',
          daily_limit: defaults.daily_limit,
          monthly_limit: defaults.monthly_limit,
          is_default: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'affiliate_id,user_id,feature_type'
        });

      if (error) {
        console.error('❌ Error resetting user limits:', error);
        return false;
      }

      console.log('✅ Successfully reset user to default limits using upsert');
      return true;
    } catch (error) {
      console.error('❌ Exception resetting user limits:', error);
      return false;
    }
  }

  /**
   * Run backfill for all affiliate users
   */
  static async backfillAffiliateUsers(): Promise<boolean> {
    try {
      
      
      const { data, error } = await supabase.rpc('backfill_affiliate_user_defaults');

      if (error) {
        console.error('❌ Error running backfill:', error);
        return false;
      }

      console.log('✅ Backfill completed:', data);
      return true;
    } catch (error) {
      console.error('❌ Exception running backfill:', error);
      return false;
    }
  }

  /**
   * Run data consistency check and fix
   */
  static async validateAndFixDataConsistency(): Promise<{
    success: boolean;
    issues: string[];
    fixed: number;
  }> {
    try {
      console.log('🔍 Running data consistency check...');
      
      const { userAssignmentService } = await import('./userAssignmentService');
      const validation = await userAssignmentService.validateDataConsistency();
      
      if (validation.isConsistent) {
        console.log('✅ Data is consistent');
        return { success: true, issues: [], fixed: 0 };
      }

      console.log('⚠️ Found consistency issues:', validation.issues);
      
      // Convert issues to string array for return type
      const issueStrings = validation.issues.map(issue => issue.description);
      
      // Run migration to fix issues
      const { AffiliateDataMigrationService } = await import('./affiliateDataMigrationService');
      const migrationResult = await AffiliateDataMigrationService.runFullMigration();
      
      return {
        success: migrationResult.success,
        issues: issueStrings,
        fixed: migrationResult.syncResult.updated + migrationResult.backfillResult.created
      };
    } catch (error) {
      console.error('❌ Exception during consistency check:', error);
      return { success: false, issues: ['Consistency check failed'], fixed: 0 };
    }
  }
}
